import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { apiCallGeneric } from "../../services/api-service";

const SearchWithFreeText = ({
  onDropdownChange,
  onChangeSearch,
  apiUrl,
  placeholder,
  selectedValue,
  selectedOption,
}) => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectIndex, setSelectIndex] = useState(null);

  const dropdownRef = useRef(null);

  // Styles
  const dropdownHeaderStyle = {
    height: "40px",
    padding: "10px",
    border: isOpen ? "1px solid #842234" : "1px solid #E5E5E5",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "#FFFFFF",
    color: selectedOption || selectedValue !== "" ? "#1E1E1E" : "#81878C",
  };

  const optionStyle = (option, index) => ({
    padding: "10px",
    cursor: "pointer",
    background:
      selectedOption && selectedOption.id === option.id && index === selectIndex
        ? "#f0f0f0"
        : "#fff",
    color: "#1E1E1E",
    textAlign: "left",
  });

  const dropdownOptionsStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    border: "1px solid #ccc",
    borderRadius: "4px",
    background: "#fff",
    zIndex: 1000,
    maxHeight: "200px",
    overflowY: "auto",
  };

  const inputStyle = {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #fff",
    boxSizing: "border-box",
    color: "#1E1E1E",
  };

  // Fetch options from API
  const fetchOptions = async () => {
    setLoading(true);
    setError(null);
    try {
      if (apiUrl) {
        const response = await apiCallGeneric("get", apiUrl, null, {
          params: { term: searchTerm },
        });

        if (response.status === 200 && response.data.success) {
          setOptions(response.data.suggestionlist);
          setFilteredOptions(response.data.suggestionlist);
        } else {
          setOptions([]);
          setFilteredOptions([]);
        }
      }
    } catch (err) {
      console.error("Error fetching options:", err);
      setError("Failed to fetch options. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle outside click
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      fetchOptions();
    }
  };

  // Handle option selection
  const selectOption = (option, index) => {
    onDropdownChange(option);
    setSelectIndex(index);
    setIsOpen(false);
  };

  // Effects
  useEffect(() => {
    if (selectedValue && typeof selectedValue === "object") {
      setSearchTerm(selectedValue.label || "");
    } else if (typeof selectedValue === "string") {
      setSearchTerm(selectedValue);
    } else {
      setSearchTerm("");
    }
  }, [selectedValue]);

  useEffect(() => {
    fetchOptions();
  }, [searchTerm]);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredOptions(options);
    } else {
      if (options.length > 0 && typeof searchTerm === "string") {
        setFilteredOptions(
          options.filter(
            (option) =>
              option.label != null &&
              option.label.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
        );
      }
    }
  }, [searchTerm, options]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // JSX
  return (
    <div ref={dropdownRef} style={{ position: "relative", height: "40px" }}>
      {/* Dropdown Header */}
      <div onClick={toggleDropdown} style={dropdownHeaderStyle}>
        <input
          type="text"
          value={selectedOption ? selectedOption.label : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            onChangeSearch(e.target.value);
          }}
          placeholder={placeholder || "Search..."}
          // style={inputStyle}
          className="dropdown-input"
        />
        <span style={{ marginLeft: "10px" }}>
          <span className="text-black">
            <ChevronDown />
          </span>
        </span>
      </div>

      {/* Dropdown Options */}
      {isOpen && (
        <div style={dropdownOptionsStyle}>
          {loading ? (
            <div style={{ padding: "10px", textAlign: "center" }}>
              Loading...
            </div>
          ) : error ? (
            <div style={{ padding: "10px", textAlign: "center", color: "red" }}>
              {error}
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                onClick={() => selectOption(option, index)}
                style={optionStyle(option, index)}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div style={{ padding: "10px", textAlign: "center" }}>
              No options found.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchWithFreeText;
